import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, errText } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { PawPrint, Loader2, Mail, KeyRound, ArrowLeft, CheckCircle2 } from "lucide-react";

export default function ForgotPassword() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [question, setQuestion] = useState(null);
  const [mode, setMode] = useState("choose"); // choose | security
  const [answer, setAnswer] = useState("");
  const [pw, setPw] = useState("");

  const sendLink = async () => {
    if (!email.trim()) return toast.error("Enter your email");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (e) { toast.error(errText(e)); }
    finally { setLoading(false); }
  };

  const checkSecurity = async () => {
    if (!email.trim()) return toast.error("Enter your email first");
    try {
      const { data } = await api.get(`/auth/security-question`, { params: { email: email.trim() } });
      if (data.question) { setQuestion(data.question); setMode("security"); }
      else toast.error("No security question is set for this account. Use the email link instead.");
    } catch (e) { toast.error(errText(e)); }
  };

  const resetViaSecurity = async () => {
    if (!answer.trim() || pw.length < 6) return toast.error("Enter your answer and a password (min 6 chars)");
    setLoading(true);
    try {
      await api.post("/auth/reset-password-security", { email: email.trim(), answer: answer.trim(), password: pw });
      toast.success("Password reset! Please sign in.");
      nav("/");
    } catch (e) { toast.error(errText(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-16" data-testid="forgot-password-page">
      <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
        <div className="flex items-center gap-2 mb-1">
          <span className="h-9 w-9 rounded-xl bg-[#F5551E] flex items-center justify-center"><PawPrint size={20} className="text-white" /></span>
          <h1 className="font-display text-xl font-bold text-[#12234A]">Reset your password</h1>
        </div>

        {sent ? (
          <div className="mt-6 text-center" data-testid="forgot-sent">
            <CheckCircle2 size={44} className="mx-auto text-emerald-500" />
            <p className="mt-3 text-slate-600">If an account exists for <strong>{email}</strong>, we've sent a password reset link. Check your inbox (and spam) — the link expires in 1 hour.</p>
            <Link to="/" className="mt-6 inline-block text-[#F5551E] font-medium">Back to home</Link>
          </div>
        ) : mode === "security" ? (
          <div className="mt-6 space-y-4" data-testid="security-reset-form">
            <p className="text-sm text-slate-500">Answer your security question to set a new password.</p>
            <div>
              <Label>{question}</Label>
              <Input value={answer} onChange={(e) => setAnswer(e.target.value)} data-testid="security-answer" className="mt-1 h-11 rounded-xl" placeholder="Your answer" />
            </div>
            <div>
              <Label>New password</Label>
              <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="security-new-password" className="mt-1 h-11 rounded-xl" placeholder="••••••••" />
            </div>
            <Button onClick={resetViaSecurity} disabled={loading} data-testid="security-reset-submit" className="w-full h-11 rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white font-semibold">
              {loading ? <Loader2 className="animate-spin" size={18} /> : "Reset password"}
            </Button>
            <button onClick={() => setMode("choose")} className="text-sm text-slate-400 flex items-center gap-1"><ArrowLeft size={14} /> Back</button>
          </div>
        ) : (
          <div className="mt-6 space-y-4">
            <p className="text-sm text-slate-500">Enter your account email and we'll send you a secure reset link.</p>
            <div>
              <Label htmlFor="fp-email">Email</Label>
              <Input id="fp-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email" className="mt-1 h-11 rounded-xl" placeholder="you@example.com" />
            </div>
            <Button onClick={sendLink} disabled={loading} data-testid="forgot-send-link" className="w-full h-11 rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white font-semibold gap-2">
              {loading ? <Loader2 className="animate-spin" size={18} /> : <><Mail size={16} /> Email me a reset link</>}
            </Button>
            <div className="flex items-center gap-3 text-xs text-slate-400"><div className="flex-1 h-px bg-slate-200" /> or <div className="flex-1 h-px bg-slate-200" /></div>
            <Button onClick={checkSecurity} variant="outline" data-testid="forgot-use-security" className="w-full h-11 rounded-xl gap-2">
              <KeyRound size={16} /> Use my security question
            </Button>
            <Link to="/" className="text-sm text-slate-400 flex items-center gap-1"><ArrowLeft size={14} /> Back to home</Link>
          </div>
        )}
      </div>
    </div>
  );
}
